import { SiteLayout } from "@/components/SiteLayout";
import { useSite } from "@/hooks/use-site";
import { localizeUrl } from "@/lib/site";
import { pickLocalized } from "@/lib/localized";
import { accessories, type Accessory, type AccessoryCategory } from "@/data/accessories";
import { ChevronRight, Video, Check, ArrowLeft, Smartphone } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

type InstructionStep = {
  num: number;
  en: string;
  uk: string;
  pl: string;
  pt: string;
  es: string;
};

type RemoteInstructions = {
  // Per-locale programming video. `en` is the required fallback; other locales
  // fall back to it when no localized video exists.
  videoUrl?: { en: string; uk?: string; pl?: string; pt?: string; es?: string };
  steps: InstructionStep[];
};

const REMOTE_INSTRUCTIONS: Record<string, RemoteInstructions> = {
  "bt-remote-tv6-ultra": {
    steps: [
      {
        num: 1,
        en: "Press and hold the white [POWER] button until the indicator starts blinking — the remote is now in programming mode and ready to receive an IR signal.",
        uk: "Натисніть і утримуйте білу кнопку [POWER], поки індикатор не почне блимати — пульт увійшов у режим програмування та готовий до прийому ІЧ-сигналу.",
        pl: "Naciśnij i przytrzymaj biały przycisk [POWER], aż wskaźnik zacznie migać — pilot jest teraz w trybie programowania i gotowy do odbioru sygnału podczerwieni.",
        pt: "Mantenha pressionado o botão branco [POWER] até o indicador começar a piscar — o controle remoto agora está em modo de programação e pronto para receber um sinal IR.",
        es: "Mantenga presionado el botón blanco [POWER] hasta que el indicador comience a parpadear — el control remoto ahora está en modo de programación y listo para recibir una señal IR.",
      },
      {
        num: 2,
        en: "Point the IR transmitters of both remotes (your TV remote and the inext BT Remote) at each other at a distance of about 1-2 cm, then press the button on your TV remote whose command you want to copy.",
        uk: "Спрямуйте ІЧ-діоди обох пультів (пульта телевізора та inext BT Remote) один на одного на відстані близько 1-2 см, потім натисніть на пульті телевізора кнопку, команду якої потрібно скопіювати.",
        pl: "Skieruj nadajniki podczerwieni obu pilotów (pilota telewizora i inext BT Remote) na siebie w odległości około 1-2 cm, a następnie naciśnij na pilocie telewizora przycisk, którego polecenie chcesz skopiować.",
        pt: "Aponte os transmissores IR dos dois controles remotos (o controle da TV e o inext BT Remote) um para o outro a uma distância de cerca de 1-2 cm e, em seguida, pressione no controle da TV o botão cujo comando você deseja copiar.",
        es: "Apunte los transmisores IR de ambos controles remotos (el control de su TV y el inext BT Remote) uno hacia el otro a una distancia de aproximadamente 1-2 cm y luego presione en el control de su TV el botón cuyo comando desea copiar.",
      },
      {
        num: 3,
        en: "If the command is accepted, the indicator will blink rapidly three times — the command has been recorded successfully.",
        uk: "Якщо команду прийнято, індикатор швидко блимне тричі — команду успішно записано.",
        pl: "Jeśli polecenie zostało zaakceptowane, wskaźnik zamiga szybko trzy razy — polecenie zostało pomyślnie zapisane.",
        pt: "Se o comando for aceito, o indicador piscará rapidamente três vezes — o comando foi gravado com sucesso.",
        es: "Si el comando es aceptado, el indicador parpadeará rápidamente tres veces — el comando se grabó correctamente.",
      },
    ],
  },
  "remote-v3": {
    videoUrl: { en: "https://www.youtube.com/embed/mM5KGyBYLY4" },
    steps: [
      {
        num: 1,
        en: "Enter the remote control programming mode by pressing the [POWER] button in the TV section. Press and hold the [POWER] button (for about 5 seconds) until the indicator stops flashing and is constantly lit.",
        uk: "Увійдіть у режим програмування пульта, натиснувши кнопку [POWER] у секції TV. Натисніть і утримуйте кнопку [POWER] (близько 5 секунд), поки індикатор не перестане блимати і почне світитися постійно.",
        pl: "Wejdź w tryb programowania pilota, naciskając przycisk [POWER] w sekcji TV. Naciśnij i przytrzymaj przycisk [POWER] (przez około 5 sekund), aż wskaźnik przestanie migać i zacznie świecić światłem ciągłym.",
        pt: "Entre no modo de programação do controle remoto pressionando o botão [POWER] na seção TV. Mantenha pressionado o botão [POWER] (por cerca de 5 segundos) até o indicador parar de piscar e ficar aceso de forma constante.",
        es: "Ingrese al modo de programación del control remoto presionando el botón [POWER] en la sección TV. Mantenga presionado el botón [POWER] (durante unos 5 segundos) hasta que el indicador deje de parpadear y quede encendido de forma constante.",
      },
      {
        num: 2,
        en: "Select a button in the inext remote control's TV section to which you need to record a command, press it once and release it, the indicator will start flashing — the remote control is ready to receive an IR signal for recording.",
        uk: "Виберіть кнопку в секції TV пульта inext, на яку потрібно записати команду, натисніть її один раз і відпустіть, індикатор почне блимати — пульт готовий до прийому ІЧ-сигналу.",
        pl: "Wybierz przycisk w sekcji TV pilota inext, na którym chcesz zapisać polecenie, naciśnij go raz i zwolnij, wskaźnik zacznie migać — pilot jest gotowy do odbioru sygnału podczerwieni.",
        pt: "Selecione na seção TV do controle remoto inext o botão no qual você precisa gravar um comando, pressione-o uma vez e solte-o; o indicador começará a piscar — o controle remoto está pronto para receber um sinal IR para gravação.",
        es: "Seleccione en la sección TV del control remoto inext el botón en el que necesita grabar un comando, presiónelo una vez y suéltelo; el indicador comenzará a parpadear — el control remoto está listo para recibir una señal IR para la grabación.",
      },
      {
        num: 3,
        en: "While pointing the IR diodes of the two remotes (TV and inext) at each other at a distance of about 1-2 cm, press once on the TV remote the desired button whose function you want to transfer. If the command was accepted, the indicator on the inext remote will flash three times quickly and start to light continuously, which means that the command has been recorded.",
        uk: "Направивши ІЧ-діоди обох пультів (телевізора та inext) один на одного на відстані близько 1-2 см, натисніть один раз на пульті телевізора кнопку, функцію якої ви хочете передати. Якщо команду прийнято, індикатор на пульті inext швидко блимне тричі та почне світитися постійно — команду записано.",
        pl: "Skieruj diody podczerwieni obu pilotów (telewizora i inext) na siebie w odległości około 1-2 cm, naciśnij raz na pilocie telewizora przycisk, którego funkcję chcesz przenieść. Jeśli polecenie zostało zaakceptowane, wskaźnik na pilocie inext zamiga szybko trzy razy i zacznie świecić światłem ciągłym — polecenie zostało zapisane.",
        pt: "Mantendo os diodos IR dos dois controles remotos (TV e inext) apontados um para o outro a uma distância de cerca de 1-2 cm, pressione uma vez no controle da TV o botão desejado cuja função você quer transferir. Se o comando for aceito, o indicador do controle inext piscará rapidamente três vezes e passará a ficar aceso de forma constante, o que significa que o comando foi gravado.",
        es: "Manteniendo los diodos IR de ambos controles remotos (TV e inext) apuntados uno hacia el otro a una distancia de aproximadamente 1-2 cm, presione una vez en el control de la TV el botón deseado cuya función desea transferir. Si el comando fue aceptado, el indicador del control inext parpadeará rápidamente tres veces y quedará encendido de forma constante, lo que significa que el comando se grabó.",
      },
      {
        num: 4,
        en: "Repeat steps 2-3 for all buttons you wish to program.",
        uk: "Повторіть кроки 2-3 для всіх кнопок, які ви хочете запрограмувати.",
        pl: "Powtórz kroki 2-3 dla wszystkich przycisków, które chcesz zaprogramować.",
        pt: "Repita os passos 2-3 para todos os botões que você deseja programar.",
        es: "Repita los pasos 2-3 para todos los botones que desee programar.",
      },
      {
        num: 5,
        en: "If you have recorded all the commands and want to exit the programming mode, press the [OK] button at the center of the control panel.",
        uk: "Якщо ви записали всі команди та хочете вийти з режиму програмування, натисніть кнопку [OK] по центру пульта.",
        pl: "Jeśli zapisałeś wszystkie polecenia i chcesz wyjść z trybu programowania, naciśnij przycisk [OK] na środku pilota.",
        pt: "Se você já gravou todos os comandos e deseja sair do modo de programação, pressione o botão [OK] no centro do controle remoto.",
        es: "Si ya grabó todos los comandos y desea salir del modo de programación, presione el botón [OK] en el centro del control remoto.",
      },
    ],
  },
  "remote-v2": {
    videoUrl: {
      en: "https://www.youtube.com/embed/f9Z8_bcaKbA",
      uk: "https://www.youtube.com/embed/4bP21iY4rV0",
    },
    steps: [
      {
        num: 1,
        en: "Press and hold the right [POWER] button in the TV Control section. The LED will light up. When it becomes brighter, release the button — programming mode is now active.",
        uk: "Натисніть і утримуйте праву кнопку [POWER] у секції керування телевізором. Світлодіодний індикатор засвітиться. Коли він почне світитися яскравіше, відпустіть кнопку — режим програмування активовано.",
        pl: "Naciśnij i przytrzymaj prawy przycisk [POWER] w sekcji sterowania telewizorem. Dioda LED zaświeci się. Gdy zacznie świecić jaśniej, zwolnij przycisk — tryb programowania jest aktywny.",
        pt: "Mantenha pressionado o botão [POWER] direito na seção de controle da TV. O LED acenderá. Quando ficar mais intenso, solte o botão — o modo de programação estará ativo.",
        es: "Mantenga presionado el botón [POWER] derecho en la sección de control de la TV. El LED se encenderá. Cuando brille con más intensidad, suelte el botón — el modo de programación estará activo.",
      },
      {
        num: 2,
        en: "Press the desired button in the TV Control section once. The LED will flash, indicating that the remote is ready to learn a command.",
        uk: "Один раз натисніть потрібну кнопку в секції керування телевізором. Індикатор почне блимати, показуючи, що пульт готовий навчитися команди.",
        pl: "Naciśnij raz wybrany przycisk w sekcji sterowania telewizorem. Dioda LED zacznie migać, sygnalizując, że pilot jest gotowy do nauczenia się polecenia.",
        pt: "Pressione uma vez o botão desejado na seção de controle da TV. O LED piscará, indicando que o controle está pronto para aprender um comando.",
        es: "Presione una vez el botón deseado en la sección de control de la TV. El LED parpadeará, indicando que el control está listo para aprender un comando.",
      },
      {
        num: 3,
        en: "Position the two remotes with their IR transmitters facing each other, 1–2 cm apart. Press the desired button on the TV remote. When the command is saved, the LED flashes three times and remains lit.",
        uk: "Розташуйте два пульти ІЧ-передавачами один до одного на відстані 1–2 см. Натисніть потрібну кнопку на пульті телевізора. Коли команду буде збережено, індикатор тричі блимне і продовжить світитися.",
        pl: "Ustaw oba piloty nadajnikami podczerwieni skierowanymi do siebie w odległości 1–2 cm. Naciśnij wybrany przycisk na pilocie telewizora. Po zapisaniu polecenia dioda LED zamiga trzykrotnie i pozostanie zapalona.",
        pt: "Posicione os dois controles com os transmissores IR voltados um para o outro, a uma distância de 1–2 cm. Pressione o botão desejado no controle da TV. Quando o comando for salvo, o LED piscará três vezes e permanecerá aceso.",
        es: "Coloque los dos controles con sus transmisores IR frente a frente, a una distancia de 1–2 cm. Presione el botón deseado en el control de la TV. Cuando se guarde el comando, el LED parpadeará tres veces y permanecerá encendido.",
      },
      {
        num: 4,
        en: "Repeat steps 2–3 for the other buttons.",
        uk: "Повторіть кроки 2–3 для інших кнопок.",
        pl: "Powtórz kroki 2–3 dla pozostałych przycisków.",
        pt: "Repita os passos 2–3 para os outros botões.",
        es: "Repita los pasos 2–3 para los demás botones.",
      },
      {
        num: 5,
        en: "Press [OK] or wait 10 seconds to exit programming mode.",
        uk: "Натисніть [OK] або зачекайте 10 секунд, щоб вийти з режиму програмування.",
        pl: "Naciśnij [OK] lub odczekaj 10 sekund, aby wyjść z trybu programowania.",
        pt: "Pressione [OK] ou aguarde 10 segundos para sair do modo de programação.",
        es: "Presione [OK] o espere 10 segundos para salir del modo de programación.",
      },
    ],
  },
  "remote-standard": {
    videoUrl: {
      en: "https://www.youtube.com/embed/ty4gupe40ng",
      uk: "https://www.youtube.com/embed/XMylecS1veI",
    },
    steps: [
      {
        num: 1,
        en: "Enter the remote control programming mode by pressing the [SET] button in the TV remote control section. Press and hold the [SET] button until the indicator light starts to glow red brighter.",
        uk: "Увійдіть у режим програмування пульта, натиснувши кнопку [SET] у TV-секції пульта. Натисніть і утримуйте кнопку [SET], поки світловий індикатор не почне світитися червоним кольором яскравіше.",
        pl: "Wejdź w tryb programowania pilota, naciskając przycisk [SET] w sekcji TV pilota. Naciśnij i przytrzymaj przycisk [SET], aż wskaźnik zacznie świecić na czerwono jaśniejszym światłem.",
        pt: "Entre no modo de programação do controle remoto pressionando o botão [SET] na seção TV do controle. Mantenha pressionado o botão [SET] até a luz indicadora começar a brilhar em vermelho com mais intensidade.",
        es: "Ingrese al modo de programación del control remoto presionando el botón [SET] en la sección TV del control. Mantenga presionado el botón [SET] hasta que la luz indicadora comience a brillar en rojo con más intensidad.",
      },
      {
        num: 2,
        en: "Select one of the buttons in the TV Remote section of the remote control to which you want to record a command, press it once and release it — the LED will start flashing, and the remote control is ready to receive the IR signal for recording.",
        uk: "Виберіть одну з кнопок у TV-секції пульта, на яку ви хочете записати команду, натисніть її один раз і відпустіть — світлодіод почне блимати, пульт готовий до запису.",
        pl: "Wybierz jeden z przycisków w sekcji TV na pilocie, do którego chcesz przypisać funkcję, naciśnij go raz i zwolnij — dioda zacznie migać, pilot jest gotowy do zapisu.",
        pt: "Selecione um dos botões na seção TV Remote do controle remoto no qual você deseja gravar um comando, pressione-o uma vez e solte-o — o LED começará a piscar e o controle remoto estará pronto para receber o sinal IR para gravação.",
        es: "Seleccione uno de los botones en la sección TV Remote del control remoto en el que desea grabar un comando, presiónelo una vez y suéltelo — el LED comenzará a parpadear y el control remoto estará listo para recibir la señal IR para la grabación.",
      },
      {
        num: 3,
        en: "With the IR LEDs of the two remotes (TV and inext) facing each other at a distance of about 1-2 cm, press once on the TV remote control the desired button whose function you want to transfer. If the command is accepted, the LED on the inext remote control blinks three times and lights steadily, which means that the command is recorded.",
        uk: "Направивши ІЧ-діоди обох пультів (телевізора та inext) один на одного на відстані близько 1-2 см, натисніть кнопку на пульті телевізора, функцію якої ви хочете передати. Якщо команду прийнято, світлодіод на пульті inext швидко блимне тричі та почне світитися постійно — команду записано.",
        pl: "Skieruj diody podczerwieni obu pilotów (telewizora i inext) na siebie w odległości około 1-2 cm, naciśnij raz na pilocie telewizora przycisk, którego funkcję chcesz przenieść. Jeśli polecenie zostało zaakceptowane, dioda na pilocie inext zamiga trzy razy i zacznie świecić ciągle — polecenie zostało zapisane.",
        pt: "Com os LEDs IR dos dois controles remotos (TV e inext) apontados um para o outro a uma distância de cerca de 1-2 cm, pressione uma vez no controle da TV o botão desejado cuja função você quer transferir. Se o comando for aceito, o LED do controle inext piscará três vezes e ficará aceso de forma constante, o que significa que o comando foi gravado.",
        es: "Con los LED IR de ambos controles remotos (TV e inext) apuntados uno hacia el otro a una distancia de aproximadamente 1-2 cm, presione una vez en el control de la TV el botón deseado cuya función desea transferir. Si el comando es aceptado, el LED del control inext parpadeará tres veces y quedará encendido de forma constante, lo que significa que el comando se grabó.",
      },
      {
        num: 4,
        en: "Repeat steps 2-3 for all the buttons you want to program.",
        uk: "Повторіть кроки 2-3 для всіх кнопок, які ви хочете запрограмувати.",
        pl: "Powtórz kroki 2-3 dla wszystkich przycisków, które chcesz zaprogramować.",
        pt: "Repita os passos 2-3 para todos os botões que você deseja programar.",
        es: "Repita los pasos 2-3 para todos los botones que desee programar.",
      },
      {
        num: 5,
        en: "If you have written all the commands and want to exit the programming mode, press the [SET] button.",
        uk: "Якщо ви записали всі команди та хочете вийти з режиму програмування, натисніть кнопку [SET].",
        pl: "Jeśli zapisałeś wszystkie polecenia i chcesz wyjść z trybu programowania, naciśnij przycisk [SET].",
        pt: "Se você já gravou todos os comandos e deseja sair do modo de programação, pressione o botão [SET].",
        es: "Si ya grabó todos los comandos y desea salir del modo de programación, presione el botón [SET].",
      },
    ],
  },
  "air-remote": {
    videoUrl: {
      en: "https://www.youtube.com/embed/stxSrUm53iE",
      uk: "https://www.youtube.com/embed/wLhsKjcZ_dM",
    },
    steps: [
      {
        num: 1,
        en: "Press and hold the [POWER] or [SET] button in the TV section of the air remote for about 3-5 seconds until the LED indicator stays lit or flashes slowly.",
        uk: "Натисніть і утримуйте кнопку [POWER] або [SET] в ТВ-секції пульта inext AIR протягом 3-5 секунд, поки світлодіодний індикатор не почне світитися постійно або повільно блимати.",
        pl: "Naciśnij i przytrzymaj przycisk [POWER] lub [SET] w sekcji TV pilota inext AIR przez około 3-5 sekund, aż wskaźnik LED zacznie świecić światłem ciągłym lub migać powoli.",
        pt: "Mantenha pressionado o botão [POWER] ou [SET] na seção TV do air remote por cerca de 3-5 segundos até o indicador LED ficar aceso de forma constante ou piscar lentamente.",
        es: "Mantenga presionado el botón [POWER] o [SET] en la sección TV del air remote durante unos 3-5 segundos hasta que el indicador LED quede encendido de forma constante o parpadee lentamente.",
      },
      {
        num: 2,
        en: "Place the TV remote and the inext AIR remote head-to-head (pointing their IR transmitters at each other) at a distance of 1-2 cm.",
        uk: "Розмістіть пульт телевізора та пульт inext AIR один навпроти одного (спрямувавши ІЧ-діоди один на одного) на відстані 1-2 см.",
        pl: "Umieść pilot telewizora i pilot inext AIR naprzeciwko siebie (kierując ich nadajniki podczerwieni na siebie) w odległości 1-2 cm.",
        pt: "Posicione o controle remoto da TV e o inext AIR remote frente a frente (apontando seus transmissores IR um para o outro) a uma distância de 1-2 cm.",
        es: "Coloque el control remoto de la TV y el inext AIR remote frente a frente (apuntando sus transmisores IR uno hacia el otro) a una distancia de 1-2 cm.",
      },
      {
        num: 3,
        en: "Press the button on the TV remote whose command you want to copy. The indicator light on the inext AIR remote will start flashing rapidly to indicate it's receiving a signal.",
        uk: "Натисніть на пульті телевізора кнопку, команду якої потрібно скопіювати. Світлодіод на пульті inext AIR почне швидко блимати, сигналізуючи про отримання сигналу.",
        pl: "Naciśnij przycisk na pilocie telewizora, którego polecenie chcesz skopiować. Wskaźnik na pilocie inext AIR zacznie szybko migać, sygnalizując odbiór sygnału.",
        pt: "Pressione no controle remoto da TV o botão cujo comando você deseja copiar. A luz indicadora do inext AIR remote começará a piscar rapidamente para indicar que está recebendo um sinal.",
        es: "Presione en el control remoto de la TV el botón cuyo comando desea copiar. La luz indicadora del inext AIR remote comenzará a parpadear rápidamente para indicar que está recibiendo una señal.",
      },
      {
        num: 4,
        en: "Press the button on the inext AIR remote where you want to store this function. The indicator will stop flashing, indicating the command is saved successfully.",
        uk: "Натисніть кнопку на пульті inext AIR, на яку ви хочете зберегти цю функцію. Індикатор перестане блимати, що вказує на успішне збереження.",
        pl: "Naciśnij przycisk na pilocie inext AIR, pod którym chcesz zapisać tę funkcję. Wskaźnik przestanie migać, co oznacza pomyślne zapisanie.",
        pt: "Pressione no inext AIR remote o botão onde você deseja armazenar essa função. O indicador parará de piscar, indicando que o comando foi salvo com sucesso.",
        es: "Presione en el inext AIR remote el botón donde desea almacenar esta función. El indicador dejará de parpadear, lo que indica que el comando se guardó correctamente.",
      },
      {
        num: 5,
        en: "Press the [SET] button (or wait a few seconds) to exit the learning/programming mode.",
        uk: "Натисніть кнопку [SET] (або зачекайте кілька секунд), щоб вийти з режиму навчання/програмування.",
        pl: "Naciśnij przycisk [SET] (lub odczekaj kilka sekund), aby wyjść z trybu uczenia się/programowania.",
        pt: "Pressione o botão [SET] (ou aguarde alguns segundos) para sair do modo de aprendizagem/programação.",
        es: "Presione el botón [SET] (o espere unos segundos) para salir del modo de aprendizaje/programación.",
      },
    ],
  },
};

// V2.1 keeps the same programmable TV-control zone as V2.0; only the two
// set-top-box shortcut buttons change from Previous/Next to Channel+/Channel−.
// The programming steps and localized videos therefore apply to both models.
REMOTE_INSTRUCTIONS["remote-v2-1"] = REMOTE_INSTRUCTIONS["remote-v2"];

export function AccessoryDetailPage({ id, initialTab }: { id: string; initialTab?: string }) {
  const { messages, site, href } = useSite();
  const locale = site.locale;
  const home = localizeUrl("/", site.locale, href);
  const accessoriesUrl = localizeUrl("/products/accessories", site.locale, href);

  const accessory = accessories.find((a) => a.id === id);

  if (!accessory) {
    return (
      <SiteLayout>
        <div className="mx-auto max-w-7xl px-6 py-20 text-center">
          <h1 className="text-2xl font-semibold">Accessory not found</h1>
        </div>
      </SiteLayout>
    );
  }

  const getLocalizedField = (item: Accessory, field: "title" | "shortDesc" | "description") =>
    pickLocalized(item, locale, field);

  const getLocalizedFeatures = (item: Accessory) =>
    pickLocalized(item, locale, "keyFeatures") || [];

  const getLocalizedSpecs = (item: Accessory) =>
    pickLocalized(item, locale, "specifications") || {};

  const getCategoryLabel = (cat: AccessoryCategory) => {
    switch (cat) {
      case "remotes":
        return messages.accessoriesFilterRemotes;
      case "cables":
        return messages.accessoriesFilterCables;
      case "power":
        return messages.accessoriesFilterPower;
      case "sensors":
        return messages.accessoriesFilterSensors;
      default:
        return cat;
    }
  };

  const itemTitle = getLocalizedField(accessory, "title");
  const itemDesc = getLocalizedField(accessory, "description");
  const itemFeatures = getLocalizedFeatures(accessory);
  const itemSpecs = getLocalizedSpecs(accessory);
  const instructions = REMOTE_INSTRUCTIONS[accessory.id];
  const descriptionLink = accessory.descriptionLink;
  const linkedAccessory = descriptionLink
    ? accessories.find(({ id: accessoryId }) => accessoryId === descriptionLink.accessoryId)
    : undefined;
  const descriptionLinkIndex = descriptionLink ? itemDesc.indexOf(descriptionLink.text) : -1;

  const renderDescription = () => {
    if (!descriptionLink || !linkedAccessory || descriptionLinkIndex < 0) return itemDesc;

    const linkedAccessoryUrl = localizeUrl(
      `/products/accessories/${linkedAccessory.id}`,
      site.locale,
      href,
    );

    return (
      <>
        {itemDesc.slice(0, descriptionLinkIndex)}
        <a
          href={linkedAccessoryUrl}
          aria-label={getLocalizedField(linkedAccessory, "title")}
          className="font-medium text-primary underline decoration-primary/35 underline-offset-4 transition-colors hover:decoration-primary"
        >
          {descriptionLink.text}
        </a>
        {itemDesc.slice(descriptionLinkIndex + descriptionLink.text.length)}
      </>
    );
  };

  const backLabel = messages.accessoriesBackToList;
  const videoLabel = messages.accessoriesVideoGuide;

  // Only surface tabs that actually have content — accessories without features or
  // programming steps (cables, power supplies, sensors) no longer show dead tabs.
  const tabs = [
    {
      value: "specs",
      label: messages.accessoriesSpecsTab,
      available: Object.keys(itemSpecs).length > 0,
    },
    {
      value: "features",
      label: messages.accessoriesFeaturesTab,
      available: itemFeatures.length > 0,
    },
    { value: "programming", label: messages.accessoriesProgrammingTab, available: !!instructions },
  ].filter((tab) => tab.available);

  const renderSpecs = () => (
    <dl className="overflow-hidden rounded-2xl border border-border/60 [&>div+div]:border-t [&>div+div]:border-border/50">
      {Object.entries(itemSpecs).map(([key, val]) => (
        <div
          key={key}
          className="grid grid-cols-[38%_62%] gap-4 px-5 py-4 odd:bg-muted/30 sm:grid-cols-[34%_66%]"
        >
          <dt className="text-sm font-medium text-muted-foreground">{key}</dt>
          <dd className="text-sm font-medium text-foreground">{val}</dd>
        </div>
      ))}
    </dl>
  );

  const renderFeatures = () => (
    <ul className="space-y-3">
      {itemFeatures.map((feat, idx) => (
        <li
          key={idx}
          className="flex items-start gap-3 rounded-2xl border border-border/60 bg-card px-5 py-4 text-sm leading-relaxed text-foreground/90"
        >
          <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Check className="h-3.5 w-3.5" />
          </span>
          <span>{feat}</span>
        </li>
      ))}
    </ul>
  );

  const videoUrl = instructions?.videoUrl
    ? (instructions.videoUrl[locale] ?? instructions.videoUrl.en)
    : undefined;

  const renderProgramming = () =>
    instructions && (
      <div className="space-y-8">
        <ol className="space-y-4">
          {instructions.steps.map((step) => {
            const stepText = step[locale];
            return (
              <li
                key={step.num}
                className="flex items-start gap-4 rounded-2xl border border-border/60 bg-card px-5 py-4 text-sm leading-relaxed text-foreground/90"
              >
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">
                  {step.num}
                </span>
                <span className="pt-0.5">{stepText}</span>
              </li>
            );
          })}
        </ol>

        {videoUrl && (
          <div className="space-y-3">
            <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <Video className="h-4 w-4 text-primary" />
              {videoLabel}
            </span>
            <div className="relative aspect-video w-full overflow-hidden rounded-2xl border border-border/60 shadow-[var(--shadow-soft)]">
              <iframe
                src={videoUrl}
                title={`${itemTitle} Programming Guide`}
                className="absolute inset-0 h-full w-full bg-black"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
          </div>
        )}
      </div>
    );

  const renderTabContent = (value: string) => {
    if (value === "specs") return renderSpecs();
    if (value === "features") return renderFeatures();
    if (value === "programming") return renderProgramming();
    return null;
  };

  return (
    <SiteLayout>
      {/* Breadcrumb */}
      <div className="border-b border-border/60 bg-background/60">
        <nav
          aria-label={messages.breadcrumbLabel}
          className="mx-auto flex max-w-7xl items-center gap-1.5 px-6 py-3 text-xs text-muted-foreground"
        >
          <a href={home} className="hover:text-foreground">
            {messages.tv6UltraBreadcrumbHome}
          </a>
          <ChevronRight aria-hidden className="h-3 w-3" />
          <a href={localizeUrl("/products", site.locale, href)} className="hover:text-foreground">
            {messages.tv6UltraBreadcrumbProducts}
          </a>
          <ChevronRight aria-hidden className="h-3 w-3" />
          <a href={accessoriesUrl} className="hover:text-foreground">
            {messages.navAccessories}
          </a>
          <ChevronRight className="h-3 w-3" />
          <span className="text-foreground">{itemTitle}</span>
        </nav>
      </div>

      <div className="mx-auto max-w-7xl animate-fade-up px-6 py-10 lg:py-16">
        {/* Back Link */}
        <a
          href={accessoriesUrl}
          className="group mb-8 inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-primary"
        >
          <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" />
          {backLabel}
        </a>

        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-16">
          {/* Image Column */}
          <div className="lg:col-span-5">
            <div className="overflow-hidden rounded-3xl border border-border/60 bg-[#f5f5f5] lg:sticky lg:top-24">
              <div className="aspect-square">
                <img
                  src={accessory.image}
                  alt={itemTitle}
                  className="h-full w-full object-contain p-8 lg:p-12"
                />
              </div>
            </div>
          </div>

          {/* Details/Tabs Column */}
          <div className="flex flex-col lg:col-span-7">
            <div className="mb-8">
              <div className="mb-4 flex flex-wrap items-center gap-3">
                <Badge
                  variant="secondary"
                  className="px-3 py-1 text-xs font-semibold uppercase tracking-wider"
                >
                  {getCategoryLabel(accessory.category)}
                </Badge>
              </div>
              <h1 className="text-3xl font-bold leading-tight tracking-tight text-foreground sm:text-4xl lg:text-5xl">
                {itemTitle}
              </h1>
              <p className="mt-5 text-base leading-relaxed text-muted-foreground sm:text-lg">
                {renderDescription()}
              </p>
            </div>

            {tabs.length > 1 ? (
              <Tabs
                defaultValue={
                  initialTab && tabs.some((t) => t.value === initialTab)
                    ? initialTab
                    : tabs[0].value
                }
                onValueChange={(val) => {
                  // Update ?tab on the real browser URL (which keeps the locale
                  // prefix). A router navigate() would rebuild from the prefix-less
                  // route tree and bounce the user to the English page.
                  if (typeof window === "undefined") return;
                  const url = new URL(window.location.href);
                  url.searchParams.set("tab", val);
                  window.history.replaceState(
                    window.history.state,
                    "",
                    `${url.pathname}${url.search}${url.hash}`,
                  );
                }}
                className="w-full"
              >
                <TabsList className="flex h-auto w-full flex-wrap gap-1.5 rounded-2xl border border-border/60 bg-muted/30 p-1.5">
                  {tabs.map((tab) => (
                    <TabsTrigger
                      key={tab.value}
                      value={tab.value}
                      className="flex-1 whitespace-nowrap rounded-xl px-3 py-2.5 text-sm font-semibold"
                    >
                      {tab.label}
                    </TabsTrigger>
                  ))}
                </TabsList>

                {tabs.map((tab) => (
                  <TabsContent
                    key={tab.value}
                    value={tab.value}
                    className="mt-6 animate-in fade-in slide-in-from-bottom-2 duration-500 focus:outline-none"
                  >
                    {renderTabContent(tab.value)}
                  </TabsContent>
                ))}
              </Tabs>
            ) : (
              tabs.length === 1 && (
                <div>
                  <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                    {tabs[0].label}
                  </h2>
                  {renderTabContent(tabs[0].value)}
                </div>
              )
            )}

            {accessory.category === "remotes" && (
              <div className="mt-8 rounded-2xl border border-border/60 bg-card p-6">
                <div className="flex items-start gap-4">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary/10">
                    <Smartphone className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-primary">
                      {messages.remoteAppMenuLabel}
                    </p>
                    <h2 className="mb-3 text-base font-semibold leading-snug text-foreground">
                      {messages.remoteAppHeroTitle}
                    </h2>
                    <ul className="mb-4 space-y-2">
                      {[
                        messages.accessoriesAppBullet1,
                        messages.accessoriesAppBullet2,
                        messages.accessoriesAppBullet3,
                      ].map((bullet) => (
                        <li
                          key={bullet}
                          className="flex items-center gap-2.5 text-sm text-muted-foreground"
                        >
                          <Check className="h-3.5 w-3.5 shrink-0 text-primary" />
                          {bullet}
                        </li>
                      ))}
                    </ul>
                    <a
                      href={localizeUrl("/solutions/inext-remote", locale, href)}
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
                    >
                      {messages.accessoriesAppCta}
                      <ChevronRight className="h-3.5 w-3.5" />
                    </a>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </SiteLayout>
  );
}
